// Screen 5 — Overlap (a connected pair). Pick a DAY (strip + month-grid jump),
// then see that day's mutual free 30-min slots. Fetch is per-day: one day is
// well under the 100-slot API cap, so it never truncates (the old 7-day fetch
// silently dropped days 4–7 — see docs/projects/when-date-selector-plan.md).
// Beyond the calendar-sync horizon, days are disabled, never shown as free.
// Tap a slot → propose (Stage 4).
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOverlap, SYNC_HORIZON_DAYS } from '../services/availability';
import { capture } from '../lib/analytics';
import { Screen, Eyebrow, Title, Spinner, ErrorState, DayStrip, MonthGrid, DurationPicker, DURATIONS } from '../components/ui';

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Duration persists for the browser session (survives navigating away and back)
// but resets to the default on a fresh visit — sessionStorage, not localStorage.
const DURATION_KEY = 'when.overlap.durationMinutes';
const DEFAULT_DURATION = 30;
function loadDuration() {
  try {
    const v = parseInt(sessionStorage.getItem(DURATION_KEY), 10);
    return DURATIONS.some((d) => d.minutes === v) ? v : DEFAULT_DURATION;
  } catch { return DEFAULT_DURATION; }
}

export default function Overlap() {
  const { principalId } = useParams();
  const navigate = useNavigate();

  const today = useMemo(() => startOfDay(new Date()), []);
  // Last selectable day: the sync horizon spans SYNC_HORIZON_DAYS from today, so
  // the final day with real data is today + (horizon - 1).
  const maxDay = useMemo(() => addDays(today, SYNC_HORIZON_DAYS - 1), [today]);
  const stripDays = useMemo(() => {
    const out = [];
    for (let i = 0; i < SYNC_HORIZON_DAYS; i++) out.push(addDays(today, i));
    return out;
  }, [today]);
  const isSelectable = (d) => d >= today && d <= maxDay;

  const [selectedDay, setSelectedDay] = useState(today);
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(today));
  const [showGrid, setShowGrid] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(loadDuration);

  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const dayStart = useMemo(() => startOfDay(selectedDay), [selectedDay]);
  const dayEnd = useMemo(() => addDays(dayStart, 1), [dayStart]);

  useEffect(() => {
    setLoading(true); setError(null);
    getOverlap(principalId, dayStart.toISOString(), dayEnd.toISOString(), { durationMinutes })
      .then((res) => { setSlots(res?.slots || []); capture('overlap_viewed'); })
      .catch(() => setError('Couldn\'t load your overlap. Try again.'))
      .finally(() => setLoading(false));
  }, [principalId, dayStart, dayEnd, durationMinutes, reloadKey]);

  const pickDuration = (m) => {
    setDurationMinutes(m);
    try { sessionStorage.setItem(DURATION_KEY, String(m)); } catch { /* ignore */ }
  };

  const pickDay = (d) => {
    setSelectedDay(d);
    setVisibleMonth(startOfMonth(d));
    setShowGrid(false);
  };

  const propose = (slot) => {
    capture('slot_proposed', { start: slot.start });
    navigate(`/with/${principalId}/propose?start=${encodeURIComponent(slot.start)}&end=${encodeURIComponent(slot.end)}`);
  };

  const canPrevMonth = startOfMonth(today) < visibleMonth;
  const canNextMonth = startOfMonth(maxDay) > visibleMonth;
  const dayLabel = selectedDay.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });

  return (
    <Screen>
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/home')} className="font-mono text-xs text-ink/40 hover:text-ink">← people</button>
        <Eyebrow>free in common</Eyebrow>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <Title className="text-2xl">{dayLabel}</Title>
        <button
          onClick={() => setShowGrid((v) => !v)}
          className="shrink-0 font-mono text-xs text-accent hover:underline"
          aria-expanded={showGrid}
        >
          {showGrid ? 'close' : 'pick a date'}
        </button>
      </div>

      <div className="mt-4">
        {showGrid ? (
          <MonthGrid
            month={visibleMonth}
            selected={selectedDay}
            onSelect={pickDay}
            isSelectable={isSelectable}
            onPrevMonth={() => setVisibleMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
            onNextMonth={() => setVisibleMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            canPrev={canPrevMonth}
            canNext={canNextMonth}
          />
        ) : (
          <DayStrip days={stripDays} selected={selectedDay} onSelect={pickDay} />
        )}
      </div>

      <div className="mt-6">
        <DurationPicker selected={durationMinutes} onSelect={pickDuration} />
      </div>

      <div className="mt-6 min-h-[12rem]">
        {loading ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : slots.length === 0 ? (
          <p className="border border-line/40 px-5 py-8 text-center font-sans text-ink/60">
            No free time in common on {selectedDay.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}. Try another day.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => (
              <button
                key={s.start}
                onClick={() => propose(s)}
                className="border border-line px-3 py-2 font-mono text-sm hover:bg-ink hover:text-paper"
              >
                {new Date(s.start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                {' – '}
                {new Date(s.end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </button>
            ))}
          </div>
        )}
      </div>

      <p className="mt-8 font-mono text-xs text-ink/40">Updated every 15 minutes.</p>
    </Screen>
  );
}
